/**
 * Lightweight reactive signal utility - drop-in replacement for
 * `@preact/signals-react` that does NOT monkey-patch React internals.
 *
 * Components subscribe to signal changes via the `useSignalValue` hook,
 * which is powered by React 18's built-in `useSyncExternalStore`.
 */
import { useCallback, useSyncExternalStore } from "react";

/* ─── Signal type ─── */
export interface Signal<T> {
  /** Read / write the current value.  Setting triggers subscriber notifications. */
  value: T;
  /** Read without subscribing (useful in async functions / event handlers). */
  peek(): T;
  /** Low-level subscribe - used by `useSignalValue`. */
  subscribe(listener: () => void): () => void;
}

/* ─── Factory ─── */
export function signal<T>(initialValue: T): Signal<T> {
  let current = initialValue;
  const listeners = new Set<() => void>();

  return {
    get value() {
      return current;
    },
    set value(newValue: T) {
      current = newValue;
      // Notify after assignment so listeners see the new value.
      listeners.forEach((fn) => fn());
    },
    peek() {
      return current;
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/* ─── React hook ─── */

/**
 * Subscribe to a signal inside a React component.
 * Returns the current value and re-renders automatically when it changes.
 *
 * ```tsx
 * const count = useSignalValue(countSignal);
 * ```
 */
export function useSignalValue<T>(sig: Signal<T>): T {
  // `subscribe` must be referentially stable so React doesn't re-subscribe
  // on every render.  `sig` is typically a module-level constant so this
  // callback almost never changes.
  const subscribe = useCallback(
    (onStoreChange: () => void) => sig.subscribe(onStoreChange),
    [sig],
  );
  return useSyncExternalStore(subscribe, () => sig.value);
}
